/**
 * previewEditReducer.js
 *
 * Pure state-transition helpers for the AI Menu Import Preview/Edit screen
 * (Feature: ai-menu-image-import). These functions operate on an
 * `Extracted_Menu` value:
 *
 *   { categories: [ { title, items: [ { title, description, price } ] } ] }
 *
 * Every helper is a PURE function: it never mutates its input and always
 * returns a brand-new menu object (structural clone of the touched path).
 * They issue ZERO catalog writes / network calls — persistence happens only
 * on explicit confirm (task 9.5). Keeping the edit algebra isolated here lets
 * task 8.2's property tests import and exercise it directly.
 *
 * Exports (all pure):
 *   - EDITABLE_ITEM_FIELDS         : the item fields an edit may target
 *   - normalizeMenu(menu)          : coerce arbitrary input to a well-formed menu
 *   - makeEmptyItem()              : a blank item ({ title:'', description:'', price:'' })
 *   - makeEmptyCategory()          : a blank category ({ title:'', items:[] })
 *   - applyEditItem(menu, c, i, field, value)   : set only that item field
 *   - applyAddItem(menu, c)                      : append a blank item (count +1)
 *   - applyDeleteItem(menu, c, i)                : remove one item (count -1)
 *   - applyReorderItem(menu, c, i, direction)    : move item up/down (permutation)
 *   - applyEditCategory(menu, c, title)          : set only the category title
 *   - applyAddCategory(menu)                     : append a blank category (count +1)
 *   - applyDeleteCategory(menu, c)               : remove one category (count -1)
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 14.1
 */

/** Item fields the Admin may edit (Req 5.2). */
export const EDITABLE_ITEM_FIELDS = ['title', 'description', 'price'];

/** A fresh, empty item with every field present as a string. */
export function makeEmptyItem() {
  return { title: '', description: '', price: '', availableDuring: [] };
}

/** A fresh, empty category with no items. */
export function makeEmptyCategory() {
  return { title: '', items: [], availableDuring: [] };
}

// Coerce any value to a string, treating null/undefined as ''. Extracted text
// is inert data, so we never interpret it — only string-coerce it (Req 4.4).
function toStr(value) {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

/**
 * Coerce arbitrary/partial input into a well-formed Extracted_Menu, preserving
 * well-formed fields verbatim and defaulting missing `description`/`price` to
 * `''`. Used to seed local state so the reducers can assume a valid shape.
 */
export function normalizeMenu(menu) {
  const categories = Array.isArray(menu && menu.categories) ? menu.categories : [];
  return {
    categories: categories.map((cat) => normalizeCategory(cat)),
  };
}

function normalizeCategory(cat) {
  const items = Array.isArray(cat && cat.items) ? cat.items : [];
  return {
    title: toStr(cat && cat.title),
    items: items.map((item) => normalizeItem(item)),
    // Meal period assignment at category level. Gemini may suggest these
    // (Phase 4); the Admin can edit them in the preview. An absent or
    // non-array value defaults to [] (all-day). Items may override per-item.
    availableDuring: Array.isArray(cat && cat.availableDuring) ? cat.availableDuring : [],
  };
}

function normalizeItem(item) {
  const normalized = {
    title: toStr(item && item.title),
    description: toStr(item && item.description),
    price: toStr(item && item.price),
    // Meal period assignment at item level. Overrides the category-level
    // availableDuring when present (non-empty). Defaults to [] which means
    // "inherit from category" (nothing stored = all-day on that product).
    availableDuring: Array.isArray(item && item.availableDuring) ? item.availableDuring : [],
  };
  // Phase 3 (Req 12.1): preserve AI-proposed modifierGroups verbatim.
  if (Array.isArray(item && item.modifierGroups)) {
    normalized.modifierGroups = item.modifierGroups;
  }
  return normalized;
}

// True when idx is a usable array index into arr.
function inBounds(arr, idx) {
  return Number.isInteger(idx) && idx >= 0 && idx < arr.length;
}

/**
 * Edit a single field of a single item. Sets ONLY the target field and leaves
 * every other item, category, and field unchanged (Req 5.2).
 * Unknown fields or out-of-range indices return the menu unchanged.
 */
export function applyEditItem(menu, catIdx, itemIdx, field, value) {
  const base = normalizeMenu(menu);
  if (!EDITABLE_ITEM_FIELDS.includes(field)) return base;
  if (!inBounds(base.categories, catIdx)) return base;
  const category = base.categories[catIdx];
  if (!inBounds(category.items, itemIdx)) return base;

  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    const items = cat.items.map((item, ii) => {
      if (ii !== itemIdx) return item;
      return { ...item, [field]: toStr(value) };
    });
    return { ...cat, items };
  });
  return { ...base, categories };
}

/**
 * Add a new blank item to a category. Increases that category's item count by
 * exactly one and leaves all other categories unchanged (Req 5.3).
 */
export function applyAddItem(menu, catIdx) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;

  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    return { ...cat, items: [...cat.items, makeEmptyItem()] };
  });
  return { ...base, categories };
}

/**
 * Delete an item from a category. Removes exactly that item and decreases the
 * count by one (Req 5.4).
 */
export function applyDeleteItem(menu, catIdx, itemIdx) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;
  const category = base.categories[catIdx];
  if (!inBounds(category.items, itemIdx)) return base;

  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    return { ...cat, items: cat.items.filter((_, ii) => ii !== itemIdx) };
  });
  return { ...base, categories };
}

/**
 * Reorder an item within its category by one position. `direction` is 'up'
 * (toward index 0) or 'down' (toward the end). The result is a permutation of
 * the same items — same multiset, no add/drop (Req 5.5). A move past either
 * edge is a no-op.
 */
export function applyReorderItem(menu, catIdx, itemIdx, direction) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;
  const category = base.categories[catIdx];
  if (!inBounds(category.items, itemIdx)) return base;

  const delta = direction === 'up' ? -1 : direction === 'down' ? 1 : 0;
  const target = itemIdx + delta;
  if (delta === 0 || !inBounds(category.items, target)) return base;

  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    const items = [...cat.items];
    const [moved] = items.splice(itemIdx, 1);
    items.splice(target, 0, moved);
    return { ...cat, items };
  });
  return { ...base, categories };
}

/**
 * Edit a category's title. Changes ONLY the title and leaves the category's
 * items and every other category unchanged (Req 5.6).
 */
export function applyEditCategory(menu, catIdx, title) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;

  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    return { ...cat, title: toStr(title) };
  });
  return { ...base, categories };
}

/**
 * Set the availableDuring (meal period) array for a category. An empty array
 * means all-day. Used to propagate meal period from category header to all
 * items in that category (items may still override individually).
 */
export function applyEditCategoryAvailableDuring(menu, catIdx, availableDuring) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;
  const windows = Array.isArray(availableDuring) ? availableDuring.filter(Boolean) : [];
  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    return { ...cat, availableDuring: windows };
  });
  return { ...base, categories };
}

/**
 * Set the availableDuring (meal period) array for a single item within a
 * category. An empty array means "inherit from category" (no per-item
 * override). Item-level overrides the category when non-empty.
 */
export function applyEditItemAvailableDuring(menu, catIdx, itemIdx, availableDuring) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;
  const category = base.categories[catIdx];
  if (!inBounds(category.items, itemIdx)) return base;
  const windows = Array.isArray(availableDuring) ? availableDuring.filter(Boolean) : [];
  const categories = base.categories.map((cat, ci) => {
    if (ci !== catIdx) return cat;
    const items = cat.items.map((item, ii) => {
      if (ii !== itemIdx) return item;
      return { ...item, availableDuring: windows };
    });
    return { ...cat, items };
  });
  return { ...base, categories };
}

/**
 * Add a new blank category. Increases the category count by exactly one
 * (Req 5.6).
 */
export function applyAddCategory(menu) {
  const base = normalizeMenu(menu);
  return { ...base, categories: [...base.categories, makeEmptyCategory()] };
}

/**
 * Delete a category (and its items). Decreases the category count by exactly
 * one (Req 5.6).
 */
export function applyDeleteCategory(menu, catIdx) {
  const base = normalizeMenu(menu);
  if (!inBounds(base.categories, catIdx)) return base;

  return {
    ...base,
    categories: base.categories.filter((_, ci) => ci !== catIdx),
  };
}

/* ---------------------------------------------------------------------------
 * Category mapping + duplicate flagging (Feature: ai-menu-image-import, Req 6)
 *
 * The helpers below are PURE and hold no state. They let the Preview/Edit
 * screen (task 9.1) map each extracted category to an existing catalog
 * category or to a brand-new one, and flag an extracted item as a potential
 * duplicate iff an existing catalog product with the SAME title already lives
 * in the SAME target category. They perform NO catalog writes and make NO
 * network calls — capturing mapping intent only. Category creation and the
 * bulk write happen later on confirm (tasks 9.3 / 9.5).
 *
 * Task 9.2's property test imports these directly, so the semantics are kept
 * small and explicit:
 *   - Titles/categories are compared case-insensitively after trimming
 *     (see `normalizeTitle`), so "Fries" and "  fries " are treated as equal.
 *   - An item with a blank title is never a duplicate (there is nothing to
 *     match on).
 *   - A flagged duplicate DEFAULTS to excluded until the Admin includes it
 *     (`defaultItemIncluded`).
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4
 * ------------------------------------------------------------------------- */

/** Category-mapping modes for an extracted category. */
export const MAP_MODE_EXISTING = 'existing'; // map onto an existing catalog category
export const MAP_MODE_NEW = 'new'; // create a brand-new category on confirm

/**
 * Normalize a title/category name for equality comparison: coerce to string,
 * trim surrounding whitespace, and lower-case. Exported so a property test can
 * use the identical oracle when asserting the duplicate relation.
 */
export function normalizeTitle(title) {
  return toStr(title).trim().toLowerCase();
}

/**
 * The default mapping for a freshly extracted category: create a new category
 * using the extracted title. The Admin can switch it to map onto an existing
 * category (Req 6.1).
 */
export function makeDefaultCategoryMapping(extractedCategory) {
  return {
    mode: MAP_MODE_NEW,
    existingName: '',
    newName: toStr(extractedCategory && extractedCategory.title),
  };
}

/**
 * Resolve the target category NAME an extracted category's items will land in,
 * given its mapping. For `existing` mode this is the chosen existing category
 * name; for `new` mode it is the typed new name; either falls back to the
 * extracted category's own title when unset. Pure.
 */
export function resolveTargetCategoryName(mapping, extractedCategory) {
  const fallback = toStr(extractedCategory && extractedCategory.title);
  if (!mapping || typeof mapping !== 'object') return fallback;
  if (mapping.mode === MAP_MODE_EXISTING) {
    return toStr(mapping.existingName) || fallback;
  }
  return toStr(mapping.newName) || fallback;
}

// Read a product's title/category regardless of whether it uses the catalog
// attribute names (ProductName/Category) or the extracted shape (title/...).
function productTitleOf(product) {
  if (!product || typeof product !== 'object') return '';
  return normalizeTitle(product.ProductName != null ? product.ProductName : product.title);
}
function productCategoryOf(product) {
  if (!product || typeof product !== 'object') return '';
  return normalizeTitle(product.Category != null ? product.Category : product.category);
}

/**
 * isDuplicateItem — true IFF an existing catalog product with the same
 * (normalized) title exists within the same (normalized) target category
 * (Req 6.2). An item with a blank title is never a duplicate.
 *
 * @param {{title?:string}} item                 the extracted item
 * @param {string} targetCategoryName            the category the item maps to
 * @param {Array<{ProductName?:string,Category?:string}>} existingProducts
 * @returns {boolean}
 */
export function isDuplicateItem(item, targetCategoryName, existingProducts) {
  const itemTitle = normalizeTitle(item && item.title);
  if (!itemTitle) return false;
  const targetCat = normalizeTitle(targetCategoryName);
  const products = Array.isArray(existingProducts) ? existingProducts : [];
  return products.some(
    (p) => productTitleOf(p) === itemTitle && productCategoryOf(p) === targetCat
  );
}

/**
 * computeDuplicateFlags — for a working menu, its per-category mappings, and
 * the existing catalog products, return a parallel 2-D boolean array where
 * `flags[catIdx][itemIdx]` is true IFF that item is a potential duplicate
 * within its mapped target category (Req 6.2). Pure; never mutates its inputs.
 *
 * @param {Object} menu                          the (possibly partial) Extracted_Menu
 * @param {Array} mappings                       per-category mappings (aligned by index)
 * @param {Array} existingProducts               existing catalog products
 * @returns {boolean[][]}
 */
export function computeDuplicateFlags(menu, mappings, existingProducts) {
  const base = normalizeMenu(menu);
  const maps = Array.isArray(mappings) ? mappings : [];
  return base.categories.map((cat, ci) => {
    const targetName = resolveTargetCategoryName(maps[ci], cat);
    return cat.items.map((item) => isDuplicateItem(item, targetName, existingProducts));
  });
}

/**
 * The default import inclusion for an item given its duplicate flag: a flagged
 * duplicate defaults to EXCLUDED (returns false); a non-duplicate defaults to
 * included (returns true). The Admin can override this per item (Req 6.3, 6.4).
 */
export function defaultItemIncluded(isDuplicate) {
  return !isDuplicate;
}

/**
 * A stable, content-based key for tracking an Admin's explicit include/exclude
 * decision for an item, independent of its row index (which shifts on
 * add/delete/reorder). Keyed by mapped target category + item title.
 */
export function itemDecisionKey(targetCategoryName, itemTitle) {
  return `${normalizeTitle(targetCategoryName)}\u0000${normalizeTitle(itemTitle)}`;
}

/* ---------------------------------------------------------------------------
 * Reviewed-item → bulk-create-product transform
 * (Feature: ai-menu-image-import, Req 7.2 — Phase 1)
 *
 * On confirm, the Preview/Edit screen turns the reviewed working menu into the
 * payload for the existing `POST /admin/products/bulk` route. The helpers
 * below are PURE: they never mutate their inputs and make NO network calls —
 * the actual write happens later in task 9.5. They reuse the existing exports
 * in this file (normalizeMenu, resolveTargetCategoryName, itemDecisionKey,
 * defaultItemIncluded) rather than duplicating any of that logic.
 *
 * Field mapping for each reviewed, non-excluded item (Req 7.2):
 *   item.title       -> ProductName
 *   item.description -> SubText   (modifier text preserved verbatim)
 *   item.price       -> Price     (normalized string -> number; '' -> 0)
 *   mapped category  -> Category  (via resolveTargetCategoryName + mapping)
 *
 * Phase 1 omits any image entirely (no `imageUrl` key). Phase 2 (task 14) is
 * the only place that will set an image, and only when one is associated.
 *
 * Requirements: 7.2
 * ------------------------------------------------------------------------- */

/**
 * Normalize an extracted price string to the catalog's numeric `Price`.
 * Strips currency symbols, thousands separators, and surrounding whitespace,
 * then parses the remaining digits/decimal point to a number. An empty or
 * unparseable price (e.g. `''`, `'$'`, `'market'`) yields `0` (Req 7.2).
 *
 * @param {string|number} price  the extracted price (e.g. '$12.00', '12', '')
 * @returns {number}             the normalized numeric price (0 when absent)
 */
export function normalizePriceToNumber(price) {
  if (typeof price === 'number') {
    return Number.isFinite(price) ? price : 0;
  }
  const str = toStr(price).trim();
  if (!str) return 0;
  // Keep only digits, a decimal point, and a leading sign; drop currency
  // symbols, whitespace, and thousands separators ("$1,234.50" -> "1234.50").
  const cleaned = str.replace(/[^0-9.\-]/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Resolve whether a reviewed item is included in the import. Looks up the
 * Admin's explicit include/exclude decision by its content-based key
 * (`itemDecisionKey`); when no explicit decision was recorded, falls back to
 * the non-duplicate default (included). Duplicate defaults (excluded) are
 * seeded into `decisions` by the Preview/Edit screen via `defaultItemIncluded`
 * (Req 6.4), so this fallback only applies to items with no recorded decision.
 *
 * @param {Object} decisions           map of itemDecisionKey -> boolean
 * @param {string} targetCategoryName  the item's mapped target category
 * @param {string} itemTitle           the item's title
 * @returns {boolean}                  true when the item should be imported
 */
export function isItemIncludedForImport(decisions, targetCategoryName, itemTitle) {
  const map = decisions && typeof decisions === 'object' ? decisions : {};
  const key = itemDecisionKey(targetCategoryName, itemTitle);
  if (Object.prototype.hasOwnProperty.call(map, key)) {
    return Boolean(map[key]);
  }
  return defaultItemIncluded(false);
}

// Build a single bulk-create product body from a reviewed item + its resolved
// target category. AvailableDuring: item-level overrides category-level;
// if both are empty the field is omitted (= all-day availability).
function itemToBulkProductBody(item, targetCategoryName, categoryAvailableDuring) {
  const itemWindows = Array.isArray(item && item.availableDuring)
    ? item.availableDuring.filter(Boolean)
    : [];
  const catWindows = Array.isArray(categoryAvailableDuring)
    ? categoryAvailableDuring.filter(Boolean)
    : [];
  // Item-level takes precedence when set; fall back to category-level.
  const effectiveWindows = itemWindows.length > 0 ? itemWindows : catWindows;
  const body = {
    ProductName: toStr(item && item.title),
    SubText: toStr(item && item.description),
    Price: normalizePriceToNumber(item && item.price),
    Category: toStr(targetCategoryName),
  };
  if (effectiveWindows.length > 0) {
    body.AvailableDuring = effectiveWindows;
  }
  return body;
}

/**
 * transformMenuToBulkProducts — turn the reviewed working menu into a flat
 * array of bulk-create product bodies for only the non-excluded items
 * (Req 7.2). Each category's items land in the category resolved from its
 * mapping (`resolveTargetCategoryName`); each included item is mapped to a
 * `{ ProductName, SubText, Price, Category, [AvailableDuring] }` body.
 * Item-level `availableDuring` overrides category-level; both empty = all-day.
 * Pure — never mutates `menu`, `mappings`, or `decisions`.
 */
export function transformMenuToBulkProducts(menu, mappings, decisions) {
  const base = normalizeMenu(menu);
  const maps = Array.isArray(mappings) ? mappings : [];
  const products = [];
  base.categories.forEach((cat, ci) => {
    const targetName = resolveTargetCategoryName(maps[ci], cat);
    cat.items.forEach((item) => {
      if (!isItemIncludedForImport(decisions, targetName, item.title)) return;
      products.push(itemToBulkProductBody(item, targetName, cat.availableDuring));
    });
  });
  return products;
}
